import { CircleAlert, EyeClosed, EyeIcon } from 'lucide-react'
import { Button } from '../../components/button'
import { Input } from '../../components/input'
import { useCredentials } from '../../hooks/setup/useCredentials'
import { useCredentialsForm } from '../../hooks/setup/useCredentialsForm'
import { CompanyType, CredentialsType } from '../../utils/types'
import './styles.scss'
import { Popup } from '../../components/popup'
import { Systems } from '../company/types'
import { useUser } from '../../hooks/setup/useUser'
import { useCompany } from '../../hooks/setup/useCompany'
import { useTheme } from '../../hooks/setup/useTheme'
import { useCustomTheme } from '../../hooks/setup/useCustomTheme'
import { ThemeChoices } from '../systemTheme/types'
import { useFinishCheckoutMutation } from '../../store/modules/checkout/api'
import { useSteps } from '../../hooks/setup/useSteps'
import { toast } from 'sonner'
import { useEffect, useState } from 'react'
import { CheckoutRequest } from '../../store/modules/checkout/types'

export const Credentials = () => {
  const { nextStep } = useSteps()
  const [handlerFinishCheckout, { isLoading: finishIsLoading }] =
    useFinishCheckoutMutation()
  const { clearErrors, errors, handleSubmit, register, setValue } =
    useCredentialsForm()
  const { register: registerCredentials } = useCredentials()
  const { getData: getDataUser } = useUser()
  const { getData: getDataCompany } = useCompany()
  const { getData: getDataTheme } = useTheme()
  const { getData: getDataCustomTheme } = useCustomTheme()
  const { getData: getDataCredentials } = useCredentials()
  const [dataCompany, setDataCompany] = useState<CompanyType | null>(null)
  const refTokenCredentials = [
    Systems.SGP,
    Systems.MIKWEB,
    Systems.ISPFY,
    Systems.BEESWEB,
  ]
  const [showPassword, setShowPassword] = useState(false)
  const [showClientSecret, setShowClientSecret] = useState(false)

  useEffect(() => {
    const loadDataCompany = async () => {
      const c = await getDataCompany()

      if (!c) return

      setDataCompany(c)
    }

    loadDataCompany()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCleanError = (type: keyof CredentialsType) => clearErrors(type)

  const handlerCredentials = (system: Systems, credentials: Record<string, string> | null): Record<string, string> => {
    if (system === Systems.HUBSOFT) {
      return {
        client_id: credentials?.['client_id'] as string,
        client_secret: credentials?.['client_secret'] as string,
        username: credentials?.['username'] as string,
        password: credentials?.['password'] as string,
      }
    }

    if (refTokenCredentials.includes(system)) {
      return {
        token: credentials?.['token'] as string,
        app: credentials?.['app'] as string,
      }
    }

    return {
      username: credentials?.['username'] as string,
      password: credentials?.['password'] as string,
    }
  }

  const handlerRegisterCredentials = async (data: CredentialsType) => {
    await registerCredentials(data)
    const [user, company, theme, customTheme, credentials] = await Promise.all([
      getDataUser(),
      getDataCompany(),
      getDataTheme(),
      getDataCustomTheme(),
      getDataCredentials(),
    ])

    delete customTheme?.['id']

    const dataCredentials: Record<string, string> = handlerCredentials(company?.system as Systems, credentials)

    const dataForm: CheckoutRequest = {
      user: {
        full_name: user?.fullName as string,
        email: user?.email as string,
        tax_id: user?.cpf.replace(/[.-]/g, '') as string,
        password: user?.password as string,
        confirm_password: user?.confirmPassword as string,
        accept_terms: user?.acceptTerm as boolean,
      },
      company: {
        name: company?.name as string,
        email: company?.email as string,
        phone: company?.phone.replace(/\D/g, '') as string,
      },
      theme: {
        theme_key:
          theme?.theme === ThemeChoices.THEME_ID_OBSIDIAN
            ? 'theme_default'
            : `theme_${theme?.theme}`,
        light_colors: customTheme as Record<string, string>,
        dark_colors: customTheme as Record<string, string>,
      },
      credentials: {
        url: credentials?.['link'] as string,
        isp_system_key: company?.system as string,
        credentials: dataCredentials,
        extra_fields: { app: credentials?.['app'] as string },
      },
    }

    if (company?.cnpj)
      dataForm.company.tax_id = company.cnpj.replace(/[./-]/g, '') as string

    handlerFinishCheckout(dataForm)
      .unwrap()
      .then(async () => await nextStep())
      .catch(() => {
        toast.error('Erro ao tentar finalizar')
      })
  }

  const renderCredentials = (system: Systems) => {
    switch (system) {
      case Systems.SGP:
        return (
          <>
            <Input
              placeholder="App"
              fullscreen
              description={
                <Popup
                  content="Nome da aplicação vinculada ao token cadastrado no SGP"
                  trigger={<CircleAlert />}
                />
              }
              error={errors?.app?.message}
              {...register('app')}
              onChange={(event) => setValue('app', event.target.value)}
              onInput={() => handleCleanError('app')}
            />

            <Input
              placeholder="Token"
              fullscreen
              error={errors?.token?.message}
              {...register('token')}
              onChange={(event) => setValue('token', event.target.value)}
              onInput={() => handleCleanError('token')}
            />
          </>
        )
      case Systems.IXC:
      case Systems.CONTROLLR:
        return (
          <>
            <Input
              placeholder="Usuário"
              fullscreen
              error={errors.user?.message}
              {...register('username')}
              onChange={(event) => setValue('username', event.target.value)}
              onInput={() => handleCleanError('username')}
            />

            <Input
              placeholder="Senha"
              fullscreen
              error={errors.password?.message}
              type={showPassword ? 'text' : 'password'}
              rightIcon={{
                icon: showPassword ? EyeIcon : EyeClosed,
                onClick: () => setShowPassword(!showPassword),
              }}
              {...register('password')}
              onChange={(event) => setValue('password', event.target.value)}
              onInput={() => handleCleanError('password')}
            />
          </>
        )
      case Systems.MIKWEB:
      case Systems.ISPFY:
      case Systems.BEESWEB:
        return (
          <>
            <Input
              placeholder="Token"
              fullscreen
              error={errors?.token?.message}
              {...register('token')}
              onChange={(event) => setValue('token', event.target.value)}
              onInput={() => handleCleanError('token')}
            />
          </>
        )
      case Systems.RECEITA_NET:
      case Systems.MKAUTH:
      case Systems.DUOBOX:
        return (
          <>
            <Input
              placeholder="Client ID"
              fullscreen
              error={errors.user?.message}
              {...register('username')}
              onChange={(event) => setValue('username', event.target.value)}
              onInput={() => handleCleanError('username')}
            />

            <Input
              placeholder="Client Secret"
              fullscreen
              error={errors.password?.message}
              type={showPassword ? 'text' : 'password'}
              rightIcon={{
                icon: showPassword ? EyeIcon : EyeClosed,
                onClick: () => setShowPassword(!showPassword),
              }}
              {...register('password')}
              onChange={(event) => setValue('password', event.target.value)}
              onInput={() => handleCleanError('password')}
            />
          </>
        )
      case Systems.HUBSOFT:
        return (
          <>
            <Input
              placeholder="Client ID"
              fullscreen
              error={errors.client_id?.message}
              {...register('client_id')}
              onChange={(event) => setValue('client_id', event.target.value)}
              onInput={() => handleCleanError('client_id')}
            />

            <Input
              placeholder="Client Secret"
              fullscreen
              error={errors.client_secret?.message}
              type={showClientSecret ? 'text' : 'password'}
              rightIcon={{
                icon: showClientSecret ? EyeIcon : EyeClosed,
                onClick: () => setShowClientSecret(!showClientSecret),
              }}
              {...register('client_secret')}
              onChange={(event) => setValue('client_secret', event.target.value)}
              onInput={() => handleCleanError('client_secret')}
            />

            <Input
              placeholder="Usuário"
              fullscreen
              error={errors.user?.message}
              {...register('username')}
              onChange={(event) => setValue('username', event.target.value)}
              onInput={() => handleCleanError('username')}
            />

            <Input
              placeholder="Senha"
              fullscreen
              error={errors.password?.message}
              type={showPassword ? 'text' : 'password'}
              rightIcon={{
                icon: showPassword ? EyeIcon : EyeClosed,
                onClick: () => setShowPassword(!showPassword),
              }}
              {...register('password')}
              onChange={(event) => setValue('password', event.target.value)}
              onInput={() => handleCleanError('password')}
            />
          </>
        )
      default:
        return <></>
    }
  }

  const renderDescriptionLink = () => {
    return (
      <div className="content-description">
        <span>
          URL da API do seu gerenciador. Ex:{' '}
          <code>https://meuprovedor.com.br</code>
        </span>
      </div>
    )
  }

  return (
    <div className="container-company-root">
      <div className="content-title">
        <strong>Credenciais</strong>

        <p>
          Para finalizar, digite as credenciais para configuração do painel.
        </p>
      </div>

      <div className="container-enter-data">
        <Input
          fullscreen
          description={
            <Popup
              content={renderDescriptionLink()}
              trigger={<CircleAlert />}
            />
          }
          placeholder="URL da API"
          error={errors?.link?.message}
          {...register('link')}
          onChange={(event) => setValue('link', event.target.value)}
          onInput={() => handleCleanError('link')}
        />

        {renderCredentials(dataCompany?.system as Systems)}

        <div className="content-btn-submit">
          <Button
            onClick={handleSubmit(handlerRegisterCredentials)}
            type="button"
            loading={finishIsLoading}
            disabled={finishIsLoading}
            className="content-btn-submit"
          >
            Finalizar
          </Button>
        </div>
      </div>
    </div>
  )
}
